import {FC, useState, useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import styled, {keyframes} from 'styled-components'

import {CheckIcon, CopyIcon, RenderIf} from 'components'

interface AnimatedCopyProps {
  text: string
  dataCy: string
  copyTimeout?: number
  ariaLabel?: string
  copiedText?: string
  iconWidth?: number
  iconHeight?: number
}

// Define keyframes for the animations
const shrink = keyframes`
  0% {
    stroke-width: 1.2;
    stroke: white;
  }
  100% {
    stroke-width: 1;
    stroke: gray;
  }
`

const moveDown = keyframes`
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(1.2px, 1.2px);
  }
`

const grow = keyframes`
  0% {
    stroke-width: 1;
    stroke: gray;
  }
  100% {
    stroke-width: 1.2;
    stroke: white;
  }
`

const moveUp = keyframes`
  0% {
    transform: translate(1.2px, 1.2px);
  }
  100% {
    transform: translate(0, 0);
  }
`

const draw = keyframes`
  0% {
    stroke-dashoffset: 100;
  }
  100% {
    stroke-dashoffset: 0;
  }
`

const CopyWrapper = styled.span`
  display: inline-flex;
  align-items: flex-end;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  max-width: 100%;
  vertical-align: middle;

  &:hover {
    .copy-icon-svg path:first-child {
      animation: ${shrink} 0.15s forwards;
    }

    .copy-icon-svg path:last-child {
      animation: ${moveDown} 0.15s forwards;
    }
  }

  &:not(:hover) {
    .copy-icon-svg path:first-child {
      animation: ${grow} 0.3s forwards;
    }

    .copy-icon-svg path:last-child {
      animation: ${moveUp} 0.3s forwards;
    }
  }

  // animate check mark appear and disappear
  .check-mark-wrapper {
    svg path {
      stroke-dasharray: 100;
      stroke-dashoffset: 100;
      animation: ${draw} 2s linear forwards;
    }
  }
`

export const AnimatedCopy: FC<AnimatedCopyProps> = ({
  text,
  dataCy,
  copyTimeout = 2000,
  ariaLabel,
  copiedText,
  iconWidth = 24,
  iconHeight = 24,
}) => {
  const [isCheckMark, setIsCheckMark] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const {t} = useTranslation(['common'])
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const resolvedCopiedText = copiedText ?? t('COPIED')

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopyFailed(false)
      setIsCheckMark(true)

      // Clear any existing timeout before setting a new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        setIsCheckMark(false)
        timeoutRef.current = null
      }, copyTimeout)
    } catch {
      setCopyFailed(true)
    }
  }

  return (
    <CopyWrapper>
      <RenderIf isTrue={isCheckMark}>
        <span
          className="relative check-mark-wrapper flex shrink-0 items-center justify-center"
          style={{width: iconWidth, height: iconHeight}}
          role="status"
          aria-live="polite"
        >
          <span
            className="bg-gray-1400 text-xs h-6 flex justify-center items-center rounded karla mx-2 px-2 whitespace-nowrap absolute right-5 text-white"
            data-cy="copied-text"
          >
            {resolvedCopiedText}
          </span>
          <CheckIcon
            width={iconWidth}
            height={iconHeight}
            strokeColor="text-green-100"
          />
        </span>
      </RenderIf>
      <RenderIf isTrue={!isCheckMark}>
        <button
          type="button"
          onClick={handleClick}
          data-cy={dataCy}
          aria-label={ariaLabel ?? t('COPY')}
          style={{width: iconWidth, height: iconHeight}}
          className="m-0 inline-flex shrink-0 cursor-pointer items-center justify-center border-none bg-none p-0 leading-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dope-cyan-bright"
        >
          <CopyIcon width={iconWidth} height={iconHeight} classes="block" />
        </button>
      </RenderIf>
      {copyFailed && (
        <>
          <span role="alert" className="w-48 max-w-full text-xs text-red-300">
            {t('COPY_FAILED_SELECT_TEXT')}
          </span>
          <input
            type="text"
            readOnly
            value={text}
            aria-label={t('TEXT_TO_COPY_MANUALLY')}
            data-cy={`${dataCy}-fallback`}
            className="w-48 max-w-full min-w-0 rounded border border-gray-600 bg-transparent text-xs text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-dope-cyan-bright"
            onFocus={event => event.currentTarget.select()}
            onClick={event => event.currentTarget.select()}
          />
        </>
      )}
    </CopyWrapper>
  )
}

export default AnimatedCopy
